import { createMemo, createSignal, For, Show } from "solid-js";
import { useNavigate } from "@tanstack/solid-router";
import { ArrowLeft, Plus, Share2, Trash2 } from "@fleet/icons";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShoppingItemRow } from "./shopping-item-row";
import { TextField } from "@fleet/ui";
import { EmptyShoppingIllustration } from "@/components/ui/illustrations";
import { NotFoundState } from "@/components/ui/not-found-state";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { api, createApiQuery } from "@/lib/api";
import {
  addManualItemMutation,
  clearCheckedItemsMutation,
  type components,
  deleteShoppingItemMutation,
  deleteShoppingListMutation,
  queryKeys,
  revokeShoppingListMutation,
  setItemCheckedMutation,
  shareShoppingListMutation,
} from "@mealplan/shared";
import type { ShoppingItem } from "@mealplan/powersync";
import {
  useShoppingItemsQuery,
  useShoppingListDetailQuery,
  useShoppingListOrgsQuery,
} from "@mealplan/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import { registerUndo } from "@/lib/undo";
import { undoMessages } from "@/lib/undo-messages";
import { QueryErrorState } from "@/components/ui/query-error-state";

export function ShoppingListDetail(props: {
  listId: string;
  /** State of the "share" dialog from the URL (`?share=1`). */
  shareOpen?: boolean;
  /** Reflects the dialog's open/close to the URL (idempotent, `replace`). */
  onShareOpenChange?: (open: boolean) => void;
}) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const navigate = useNavigate();

  const listId = (): string => props.listId;

  const list = useShoppingListDetailQuery({ db, drizzle, userId: session.userId, listId });
  const items = useShoppingItemsQuery({
    db,
    drizzle,
    userId: session.userId,
    listId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });
  const sharedOrgs = useShoppingListOrgsQuery({ db, drizzle, userId: session.userId, listId });

  const [newItemName, setNewItemName] = createSignal("");
  const [newItemQty, setNewItemQty] = createSignal("");
  const [newItemCategory, setNewItemCategory] = createSignal("");
  // The "share" dialog is URL-addressed (`?share=1`); the route is the
  // source of truth so the link is copyable and reopenable.
  const shareOpen = (): boolean => Boolean(props.shareOpen);
  const setShareOpen = (open: boolean): void => props.onShareOpenChange?.(open);

  // User organizations (for sharing) — template API.
  const myOrgs = createApiQuery<
    components["schemas"]["OrganizationDTO"][] | null,
    components["schemas"]["ErrorModel"]
  >(
    () => queryKeys.auth.organizations(),
    async () => api.GET("/api/org/listOrganizations", {}),
  );

  const isOwner = createMemo(() => list.data?.user_id === session.userId());

  // Resolves the list: present / loading (sync) / not-found / offline.
  // Avoids leaving "Shopping list" + empty list forever when the list does
  // not exist, was deleted, or has not synced / no access.
  const resolution = createEntityResolution({
    db,
    hasEntity: () => Boolean(list.data),
    isPending: () => list.isLoading,
  });

  const groups = createMemo(() => {
    const byCategory = new Map<string, ShoppingItem[]>();
    for (const item of items.data ?? []) {
      const category = item.category?.trim() || t(msg`Other`);
      const bucket = byCategory.get(category) ?? [];
      bucket.push(item);
      byCategory.set(category, bucket);
    }
    return [...byCategory.entries()].toSorted(([a], [b]) => a.localeCompare(b));
  });

  const pendingCount = createMemo(
    () => (items.data ?? []).filter((item) => item.checked !== 1).length,
  );

  const sharedOrgIds = createMemo(
    () => new Set((sharedOrgs.data ?? []).map((row) => row.organization_id)),
  );

  const handleAddManual = async (e: Event): Promise<void> => {
    e.preventDefault();
    const dbInstance = drizzle();
    const ownerId = list.data?.user_id;
    const name = newItemName().trim();
    if (!dbInstance || !ownerId || !name) {
      return;
    }
    const qty = Number(newItemQty().replace(",", "."));
    const newId = await addManualItemMutation(dbInstance, ownerId, listId(), {
      name,
      quantity: Number.isFinite(qty) && qty > 0 ? qty : null,
      category: newItemCategory(),
    });
    registerUndo(undoMessages(t, t(msg`Item added`)), () =>
      deleteShoppingItemMutation(dbInstance, newId),
    );
    setNewItemName("");
    setNewItemQty("");
    setNewItemCategory("");
  };

  const handleToggleItem = (item: ShoppingItem, checked: boolean): void => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    void (async () => {
      try {
        await setItemCheckedMutation(dbInstance, item.id, checked);
        // Primary undo path for checking: L1 pill (or Ctrl+Z) reverts the toggle.
        registerUndo(
          undoMessages(t, checked ? t(msg`Marked as bought`) : t(msg`Unmarked`)),
          async () => {
            await setItemCheckedMutation(dbInstance, item.id, !checked);
          },
        );
      } catch (err: unknown) {
        // `setItemCheckedMutation` can throw (unverified local-first write,
        // see `verified-write.ts`): without this catch it would be an
        // unhandled rejection inside the `void (async () => {...})()`.
        console.error("Error toggling shopping item:", err);
      }
    })();
  };

  const handleDeleteItem = (id: string): void => {
    const dbInstance = drizzle();
    const ownerId = list.data?.user_id;
    if (!dbInstance || !ownerId) {
      return;
    }
    // Snapshot for the inverse (re-add the deleted item). We capture the
    // listId NOW (outside the deferred inverse) to avoid reading a signal in an
    // untracked scope.
    const currentListId = listId();
    const snapshot = (items.data ?? []).find((row) => row.id === id);
    void (async () => {
      await deleteShoppingItemMutation(dbInstance, id);
      registerUndo(undoMessages(t, t(msg`Item removed`)), async () => {
        await addManualItemMutation(dbInstance, ownerId, currentListId, {
          name: snapshot?.name ?? "",
          quantity: snapshot?.quantity ?? null,
          unit: snapshot?.unit ?? null,
          category: snapshot?.category ?? null,
        });
      });
    })();
  };

  const handleToggleShare = async (orgId: string): Promise<void> => {
    const dbInstance = drizzle();
    const uid = session.userId();
    if (!dbInstance || !uid) {
      return;
    }
    if (sharedOrgIds().has(orgId)) {
      await revokeShoppingListMutation(dbInstance, listId(), orgId);
    } else {
      await shareShoppingListMutation(dbInstance, uid, listId(), orgId);
    }
  };

  return (
    <Show
      when={resolution() === "ready" || resolution() === "loading"}
      fallback={
        <NotFoundState
          variant={resolution() === "offline" ? "offline" : "not-found"}
          title={resolution() === "offline" ? undefined : t(msg`Shopping list not found`)}
          description={
            resolution() === "offline"
              ? undefined
              : t(msg`This list may have been deleted, or you don't have access to it.`)
          }
          primaryAction={{
            label: t(msg`Back to shopping lists`),
            onClick: () => {
              void navigate({ to: "/shopping" });
            },
          }}
        />
      }
    >
      <div class="space-y-6">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="flex min-w-0 items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              aria-label={t(msg`Back`)}
              onClick={() => void navigate({ to: "/shopping" })}
            >
              <ArrowLeft class="h-4 w-4" />
            </Button>
            <div class="min-w-0">
              <h2
                class="truncate font-serif text-2xl font-semibold tracking-tight"
                data-testid="shopping-title"
              >
                {list.data?.name ?? t(msg`Shopping list`)}
              </h2>
              <p class="text-xs text-muted-foreground">
                {t(msg`{count} items pending`, { count: pendingCount() })}
              </p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <Show when={isOwner()}>
              <Button
                variant="outline"
                size="sm"
                class="gap-2"
                onClick={() => setShareOpen(true)}
                data-testid="share-list"
              >
                <Share2 class="h-4 w-4" />
                {t(msg`Share`)}
                <Show when={sharedOrgIds().size > 0}>
                  <span class="text-xs text-muted-foreground">({sharedOrgIds().size})</span>
                </Show>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const dbInstance = drizzle();
                  if (dbInstance) {
                    void clearCheckedItemsMutation(dbInstance, listId());
                  }
                }}
              >
                {t(msg`Clear bought`)}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                class="text-destructive"
                aria-label={t(msg`Delete list`)}
                onClick={() => {
                  const dbInstance = drizzle();
                  if (dbInstance) {
                    void deleteShoppingListMutation(dbInstance, listId()).then(async () =>
                      navigate({ to: "/shopping" }),
                    );
                  }
                }}
              >
                <Trash2 class="h-4 w-4" />
              </Button>
            </Show>
          </div>
        </div>

        <form class="flex flex-wrap items-center gap-2" onSubmit={(e) => void handleAddManual(e)}>
          <TextField
            class="w-20 shrink-0"
            placeholder={t(msg`Qty`)}
            value={newItemQty()}
            onInput={(e) => setNewItemQty(e.currentTarget.value)}
            data-testid="manual-qty"
          />
          <TextField
            class="min-w-40 flex-1"
            placeholder={t(msg`Add item manually...`)}
            value={newItemName()}
            onInput={(e) => setNewItemName(e.currentTarget.value)}
            data-testid="manual-name"
          />
          <TextField
            class="w-32 shrink-0"
            placeholder={t(msg`Category`)}
            value={newItemCategory()}
            onInput={(e) => setNewItemCategory(e.currentTarget.value)}
            data-testid="manual-category"
          />
          <Button
            type="submit"
            size="sm"
            class="shrink-0 gap-1"
            disabled={!drizzle() || !list.data?.user_id || !newItemName().trim()}
            data-testid="manual-add"
          >
            <Plus class="h-4 w-4" />
            {t(msg`Add`)}
          </Button>
        </form>

        <Show
          when={(items.data ?? []).length > 0}
          fallback={
            <Show when={!items.isLoading}>
              <div class="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border p-12 text-center">
                <EmptyShoppingIllustration class="mb-4 h-28 w-36 text-muted-foreground" />
                <p class="text-sm text-muted-foreground">{t(msg`This list is empty.`)}</p>
              </div>
            </Show>
          }
        >
          <div class="space-y-5">
            <For each={groups()}>
              {([category, categoryItems]) => (
                <section data-testid={`category-${category}`}>
                  <h3 class="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {category}
                  </h3>
                  <ul class="space-y-1">
                    <For each={categoryItems}>
                      {(item) => (
                        <ShoppingItemRow
                          item={item}
                          onToggle={handleToggleItem}
                          onDelete={handleDeleteItem}
                        />
                      )}
                    </For>
                  </ul>
                </section>
              )}
            </For>
          </div>
        </Show>

        <Dialog open={shareOpen()} onOpenChange={setShareOpen}>
          <DialogHeader>
            <DialogTitle>{t(msg`Share with your household`)}</DialogTitle>
          </DialogHeader>
          <div class="space-y-2 p-1">
            <p class="text-sm text-muted-foreground">
              {t(msg`Members of the selected organizations can see and check items in real time.`)}
            </p>
            {/*
              P23 / CC-60 · I2 + I3. Ésta es la única query de RED del dominio
              (el resto de la pantalla es PowerSync local-first). Sin guarda,
              con `/api/org/listOrganizations` caído el diálogo afirmaba «no
              perteneces a ninguna organización» y ofrecía crear una — cuando la
              casa del usuario sí existe y lo único que falla es la conexión.
            */}
            <Show when={myOrgs.isError}>
              <QueryErrorState
                testId="share-orgs-error"
                compact
                onRetry={() => void myOrgs.refetch()}
              />
            </Show>
            <Show when={!myOrgs.isError}>
              <For each={myOrgs.isSuccess ? (myOrgs.data ?? []) : []}>
                {(org) => (
                  <label class="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-accent/10">
                    <Checkbox
                      checked={sharedOrgIds().has(org.id)}
                      onChange={() => void handleToggleShare(org.id)}
                    />
                    <span class="text-sm">{org.name}</span>
                  </label>
                )}
              </For>
              <Show when={myOrgs.isSuccess && (myOrgs.data ?? []).length === 0}>
                <p class="text-sm text-muted-foreground">
                  {t(msg`You don't belong to any organization yet. Create one in Organizations.`)}
                </p>
              </Show>
            </Show>
          </div>
        </Dialog>
      </div>
    </Show>
  );
}
